/**
 * Stadium Upgrades API Service
 * Implementa los contratos oficiales del backend descritos en stadium-upgrades-openapi.yaml y club-service-openapi.yaml:
 *
 * GET  /api/clubs/{clubId}/stadium
 * GET  /api/clubs/{clubId}/stadium/upgrades/catalog
 * GET  /api/clubs/{clubId}/stadium/upgrades/construction
 * GET  /api/clubs/{clubId}/stadium/upgrades/quote
 * POST /api/clubs/{clubId}/stadium/upgrades
 * GET  /api/clubs/{clubId}/stadium/upgrades/{operationId}
 * GET  /api/clubs/{clubId}/stadium/upgrades/{operationId}/construction
 * PUT  /api/clubs/{clubId}/stadium/upgrades/{operationId}/cancellation
 */
import { apiSimulator } from './api-simulator.service';
import {
  BackendStadiumResponse,
  StadiumCatalogResponse,
  StadiumUpgradeQuoteResponse,
  StadiumUpgradeContractRequest,
  UpgradeOperation,
  ConstructionStatus,
  StadiumUpgradeModule,
  BackendTicketPriceResponse,
} from '../models/types';

export const stadiumUpgradesApi = {
  /**
   * GET /api/clubs/{clubId}/stadium/tickets
   * Consultar el precio de entrada vigente y su versión propia.
   */
  async getTicketPrice(clubId: string): Promise<BackendTicketPriceResponse> {
    return apiSimulator.callWithMockFallback<BackendTicketPriceResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/tickets`,
      service: 'stadium-economy',
      mockName: 'update-tickets',
      transformMock: (mock) => ({
        clubId,
        ticketPrice: mock.ticketPrice ?? 35,
        version: mock.version ?? 1,
        effectiveAt: mock.effectiveAt ?? new Date().toISOString(),
      }),
    });
  },

  /**
   * PUT /api/clubs/{clubId}/stadium/tickets
   * Modificar el precio de las entradas vigentes.
   * Envía {"ticketPrice": number, "expectedVersion"?: number}
   */
  async updateTicketPrice(
    clubId: string,
    ticketPrice: number,
    expectedVersion?: number
  ): Promise<BackendTicketPriceResponse> {
    const numPrice = Math.max(1, Math.round(Number(ticketPrice)));
    const body: { ticketPrice: number; expectedVersion?: number } = { ticketPrice: numPrice };
    if (expectedVersion !== undefined && expectedVersion !== null) {
      body.expectedVersion = expectedVersion;
    }

    return apiSimulator.callWithMockFallback<BackendTicketPriceResponse>({
      method: 'PUT',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/tickets`,
      body,
      service: 'stadium-economy',
      mockName: 'update-tickets',
      transformMock: (mock) => ({
        clubId,
        ticketPrice,
        version: (expectedVersion !== undefined ? expectedVersion + 1 : (mock.version ?? 1) + 1),
        effectiveAt: new Date().toISOString(),
      }),
    });
  },
  /**
   * GET /api/clubs/{clubId}/stadium
   * Consultar estadio efectivo: capacity, standsLevel, parkingLevel, cafeteriaLevel, merchandisingLevel, bathroomLevel y version.
   */
  async getStadium(clubId: string): Promise<BackendStadiumResponse> {
    return apiSimulator.callWithMockFallback<BackendStadiumResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium`,
      service: 'clubs',
      mockName: 'club-stadium',
      transformMock: (mock) => ({
        ...mock,
        clubId,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/catalog
   * Consultar opciones de remodelación: initialCapacity, maximumCapacity, upgrades[]
   */
  async getCatalog(clubId: string): Promise<StadiumCatalogResponse> {
    return apiSimulator.callWithMockFallback<StadiumCatalogResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades/catalog`,
      service: 'stadium-economy',
      mockName: 'upgrades-catalog',
      transformMock: (mock) => ({
        ...mock,
        clubId,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/construction
   * Consultar obra activa por club (no requiere ID de operación).
   * Devuelve 200 con operation, schedule, completionMatchdayNumber y remainingMatchdays (o null si no hay obra).
   */
  async getActiveConstruction(clubId: string): Promise<ConstructionStatus> {
    return apiSimulator.callWithMockFallback<ConstructionStatus>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades/construction`,
      service: 'stadium-economy',
      mockName: 'upgrades-construction',
      transformMock: (mock) => ({
        ...mock,
        clubId,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/quote?module={module}&upgradeCode={upgradeCode}
   * Presupuestar mejora.
   * - STANDS requiere module=STANDS&upgradeCode={code}.
   * - Instalaciones (PARKING, CAFETERIA, MERCHANDISING, BATHROOMS) solo requieren module={module}.
   */
  async getQuote(
    clubId: string,
    module: StadiumUpgradeModule,
    upgradeCode?: string
  ): Promise<StadiumUpgradeQuoteResponse> {
    let endpoint = `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades/quote?module=${encodeURIComponent(module)}`;
    if (upgradeCode) {
      endpoint += `&upgradeCode=${encodeURIComponent(upgradeCode)}`;
    }

    return apiSimulator.callWithMockFallback<StadiumUpgradeQuoteResponse>({
      method: 'GET',
      endpoint,
      service: 'stadium-economy',
      mockName: 'upgrades-quote',
      transformMock: (mock) => ({
        ...mock,
        module,
        upgradeCode: upgradeCode || mock.upgradeCode,
      }),
    });
  },

  /**
   * POST /api/clubs/{clubId}/stadium/upgrades
   * Contratar obra de remodelación.
   * Responde 202 Accepted con la operación creada.
   */
  async requestUpgrade(
    clubId: string,
    body: StadiumUpgradeContractRequest
  ): Promise<UpgradeOperation> {
    return apiSimulator.callWithMockFallback<UpgradeOperation>({
      method: 'POST',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades`,
      body,
      service: 'stadium-economy',
      mockName: 'upgrades-operation',
      transformMock: (mock) => ({
        ...mock,
        id: body.operationId,
        clubId,
        module: body.module,
        quotedCost: body.quotedCost,
        ruleSetVersion: body.ruleSetVersion,
        stadiumVersion: body.expectedStadiumVersion,
        status: 'REQUESTED' as const,
        requestedAt: new Date().toISOString(),
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/{operationId}
   * Consultar estado terminal o actual de una operación conocida (APPLIED, FAILED, CANCELLED, etc.)
   */
  async getOperation(clubId: string, operationId: string): Promise<UpgradeOperation> {
    return apiSimulator.callWithMockFallback<UpgradeOperation>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades/${encodeURIComponent(operationId)}`,
      service: 'stadium-economy',
      mockName: 'upgrades-operation',
      transformMock: (mock) => ({
        ...mock,
        id: operationId,
        clubId,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/{operationId}/construction
   * Detalle del calendario congelado de una operación específica.
   */
  async getOperationConstruction(
    clubId: string,
    operationId: string
  ): Promise<ConstructionStatus> {
    return apiSimulator.callWithMockFallback<ConstructionStatus>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades/${encodeURIComponent(operationId)}/construction`,
      service: 'stadium-economy',
      mockName: 'upgrades-construction',
      transformMock: (mock) => ({
        ...mock,
        clubId,
      }),
    });
  },

  /**
   * PUT /api/clubs/{clubId}/stadium/upgrades/{operationId}/cancellation
   * Solicitar cancelación de una obra en estado REQUESTED o FUNDS_CONFIRMED.
   * Envía { "expectedVersion": operation.version } (versión de la operación, NO stadiumVersion).
   * Devuelve 202 Accepted.
   */
  async cancelUpgrade(
    clubId: string,
    operationId: string,
    expectedVersion: number
  ): Promise<{ status: string; version?: number; success?: boolean }> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium/upgrades/${encodeURIComponent(operationId)}/cancellation`,
      body: { expectedVersion },
      service: 'stadium-economy',
      mockName: 'upgrades-operation',
      transformMock: () => ({
        status: 'CANCEL_REQUESTED',
        version: expectedVersion + 1,
        success: true,
      }),
    });
  },
};
